import { handleAxiosError } from '@/api/helpers';
import { FiltersType } from '@/api/types';
import { client } from '@/client';
import { ExportType, ExportUsersFormValues } from '@/client/reports';
import { UsersExportFormValues } from '@/client/users';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { getUserExportColumns } from '@/utils/helpers';
import { AxiosError } from 'axios';
import { Field, Form, Formik } from 'formik';
import { Dialog, DialogProps } from 'primereact/dialog';
import { RadioButtonChangeEvent } from 'primereact/radiobutton';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { v4 } from 'uuid';
import { FormikRadio } from '../form';
import { ExportContext } from '../reports/ReportMessage';

const StyledDialog = styled(Dialog)`
  width: 500px;
`;

type UsersExportModalProps = {
  defaultFilters: FiltersType | undefined;
  filters: FiltersType | undefined;
  visibleColumns: string[];
  branchId?: string;
  groupId?: string;
} & DialogProps;

export const UsersExportModal: React.FC<UsersExportModalProps> = ({
  defaultFilters,
  filters,
  visibleColumns,
  onHide,
  visible,
  branchId,
  groupId,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const account = useAppSelector(selectCurrentAccount);
  const [initialValues] = useState<ExportUsersFormValues>({
    isAllData: true,
    areAllColumns: true,
  });

  const {
    setType,
    setExportKey,
    setIsDownloading,
    setNotificationParam,
    isConnected,
  } = useContext(ExportContext);

  const exportColumns = getUserExportColumns(visibleColumns);

  const exportData = ({
    isAllData,
    areAllColumns,
  }: {
    isAllData: boolean;
    areAllColumns: boolean;
  }) => {
    if (!account?.id) return;
    const usersExportKey = v4();

    if (branchId) {
      client.branches.exportUsers({
        accountId: account?.id,
        filters: isAllData
          ? defaultFilters
          : [...(defaultFilters ?? []), ...(filters ?? [])],
        exportKey: usersExportKey,
        branchId,
        columns: areAllColumns ? undefined : exportColumns,
      });
    } else if (groupId) {
      client.groups.exportUsers({
        filters: isAllData
          ? defaultFilters
          : [...(defaultFilters ?? []), ...(filters ?? [])],
        exportKey: usersExportKey,
        groupId,
        accountId: account?.id,
        columns: areAllColumns ? undefined : exportColumns,
      });
    } else {
      client.users.exportUsers({
        filters: isAllData
          ? defaultFilters
          : [...(defaultFilters ?? []), ...(filters ?? [])],
        exportKey: usersExportKey,
        accountId: account?.id,
        columns: areAllColumns ? undefined : exportColumns,
      });
    }

    setExportKey(usersExportKey);
  };

  const exportUsers = async ({
    isAllData,
    areAllColumns,
  }: UsersExportFormValues) => {
    if (account) {
      onHide();
      setIsDownloading(true);
      setExportKey(undefined);

      if (branchId) {
        setType(ExportType.BRANCH_USERS);
        setNotificationParam(branchId);
      } else if (groupId) {
        setType(ExportType.GROUP_USERS);
        setNotificationParam(groupId);
      } else {
        setType(ExportType.ACCOUNT_USERS);
        setNotificationParam(account?.id);
      }

      // Wait for a notify connection to establish
      await new Promise((resolve) => {
        const intervalId = setInterval(() => {
          if (isConnected) {
            clearInterval(intervalId);
            resolve(true);
          }
        }, 100);
      });

      try {
        if (isConnected) {
          exportData({ isAllData, areAllColumns });
        }
      } catch (e) {
        setIsDownloading(false);
        handleAxiosError(e as Error | AxiosError, toast);
      }
    }
  };

  return (
    <StyledDialog
      blockScroll
      visible={visible}
      header={<h1>{t('generic.export')}</h1>}
      onHide={onHide}
      draggable={false}
      data-testid="reports-export-modal"
    >
      <Formik initialValues={initialValues} onSubmit={exportUsers}>
        {({ setFieldValue, values }) => (
          <Form>
            <label htmlFor="data">{t('reports.export.data')}</label>
            <FlexContainer
              id="data"
              gap={24}
              justify="flex-start"
              className="mt-1 mb-4 w-8"
            >
              <div className="field-radio flex-1" data-id="allData">
                <Field
                  id="allData"
                  type="radio"
                  name="allData"
                  value={true}
                  label={t('reports.export.allData')}
                  shapeType="circle"
                  onChange={(e: RadioButtonChangeEvent) =>
                    setFieldValue('isAllData', e.value)
                  }
                  checked={!!values.isAllData}
                  component={FormikRadio}
                />
              </div>
              <div className="field-radio flex-1" data-id="filteredData">
                <Field
                  id="filteredData"
                  type="radio"
                  name="filteredData"
                  value={false}
                  label={t('reports.export.filteredData')}
                  shapeType="circle"
                  onChange={(e: RadioButtonChangeEvent) =>
                    setFieldValue('isAllData', e.value)
                  }
                  checked={!values.isAllData}
                  component={FormikRadio}
                />
              </div>
            </FlexContainer>

            <label htmlFor="columns">{t('reports.export.columns')}</label>
            <FlexContainer
              id="columns"
              gap={24}
              justify="flex-start"
              className="mt-1 mb-4 w-8"
            >
              <div className="field-radio flex-1" data-id="allColumns">
                <Field
                  id="allColumns"
                  name="allColumns"
                  type="radio"
                  value={true}
                  label={t('reports.export.allColumns')}
                  shapeType="circle"
                  onChange={(e: RadioButtonChangeEvent) =>
                    setFieldValue('areAllColumns', e.value)
                  }
                  checked={!!values.areAllColumns}
                  component={FormikRadio}
                />
              </div>
              <div className="field-radio flex-1" data-id="visibleColumns">
                <Field
                  id="visibleColumns"
                  name="visibleColumns"
                  type="radio"
                  value={false}
                  label={t('reports.export.visibleColumns')}
                  shapeType="circle"
                  onChange={(e: RadioButtonChangeEvent) =>
                    setFieldValue('areAllColumns', e.value)
                  }
                  checked={!values.areAllColumns}
                  component={FormikRadio}
                />
              </div>
            </FlexContainer>

            <FlexContainer justify="flex-end" className="mt-5">
              <AppButton
                label={t('button.cancel')}
                severity="secondary"
                type="outlined"
                onClick={onHide}
                className="mr-3"
                data-testid="reports-export-cancel-form"
              />
              <AppButton
                icon="pi pi-download"
                label={t('generic.export')}
                isSubmit
                data-testid="reports-export-submit-form"
              />
            </FlexContainer>
          </Form>
        )}
      </Formik>
    </StyledDialog>
  );
};
